import { useCallback, useMemo } from 'react';
import { apiErrorMessage, writeErrorMessage } from '@/lib/apiError';
import { useMyPreferences, useUpdatePreferences } from '@/features/settings/hooks/usePreferences';

/** The most pins the sidebar will hold — the API caps the document the same. */
export const MAX_PINS = 8;

/**
 * Personal sidebar shortcuts (2026-09-25, Michael: "Allow a few personal
 * shortcuts. Someone entering time daily can pin Time Entry; someone
 * processing invoices can pin Invoices.").
 *
 * Stored on the ACCOUNT (the preferences document, `nav_pins`), not in
 * localStorage like the collapse state: a shortcut you set at the desk should
 * be on your phone too. The list holds routes only; the sidebar resolves them
 * against the nav config, so a route that no longer exists — or one the
 * person may no longer open — simply does not render, and is dropped the
 * next time they change a pin.
 */
export function useNavPins(): {
  pins: string[];
  isPinned: (to: string) => boolean;
  toggle: (to: string) => void;
  full: boolean;
  saving: boolean;
  /** Why the last pin change was not saved, in the server's words — null once one succeeds. */
  error: string | null;
} {
  const { data } = useMyPreferences();
  const update = useUpdatePreferences();
  // `prefs` is optional-chained too: a test double, or a failed read, can
  // hand back a payload with no document — no pins is the right answer then.
  const pins = useMemo(() => (data?.prefs?.nav_pins ?? []).filter((p) => typeof p === 'string'), [data]);

  const isPinned = useCallback((to: string) => pins.includes(to), [pins]);

  const toggle = useCallback((to: string) => {
    const current = data?.prefs ?? {};
    const next = pins.includes(to) ? pins.filter((p) => p !== to) : [...pins, to].slice(0, MAX_PINS);
    // PUT replaces the whole document — carry every other preference across.
    update.mutate({ ...current, nav_pins: next });
  }, [data, pins, update]);

  // A refused save (the 2026-09-28 422) must not be a star that silently
  // does nothing: the strip prints this beside the tabs.
  const error = update.isError ? (apiErrorMessage(update.error) ?? writeErrorMessage(update.error)) : null;

  return { pins, isPinned, toggle, full: pins.length >= MAX_PINS, saving: update.isPending, error };
}
